import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

export default function OtpCodeEntry(props) {
  const [otp, setOtp] = useState(Array(props.length).fill("")); // Array of empty strings
  const { onChange } = props;
  const inputRefs = useRef([]); // Array of refs for each input field
  const onChangeRef = useRef(onChange);
  const lastIndex = props.length - 1;

  const handleKeyDown = (e) => {
    const { target } = e;
    const selectedIndex = inputRefs.current.indexOf(target);
    const isDigitKey = /^\d$/.test(e.key);

    if (selectedIndex < 0) {
      return;
    }

    // Keep native copy/cut/paste/select-all shortcuts.
    if ((e.metaKey || e.ctrlKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) {
      return;
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (selectedIndex > 0) {
        inputRefs.current[selectedIndex - 1].focus();
      }
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (selectedIndex < lastIndex) {
        inputRefs.current[selectedIndex + 1].focus();
      }
      return;
    }

    // Ensure key typing always updates this cell and advances focus,
    // including when typing the same existing digit (onChange may not fire).
    if (isDigitKey) {
      e.preventDefault();
      setOtp((prevOtp) => {
        const nextOtp = [...prevOtp];
        nextOtp[selectedIndex] = e.key;
        return nextOtp;
      });
      if (selectedIndex < lastIndex) {
        inputRefs.current[selectedIndex + 1]?.focus();
      }
      return;
    }

    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      setOtp((prevOtp) => {
        const nextOtp = [...prevOtp];
        nextOtp[selectedIndex] = "";
        return nextOtp;
      });
      if (selectedIndex > 0) {
        inputRefs.current[selectedIndex - 1].focus();
      }
    }
  };

  const updateFromText = (index, rawText) => {
    const text = (rawText || "").replace(/\D+/g, "");
    if (!text) {
      setOtp((prevOtp) => {
        const nextOtp = [...prevOtp];
        nextOtp[index] = "";
        return nextOtp;
      });
      return;
    }

    const nextFocus = Math.min(index + text.length - 1, lastIndex);

    setOtp((prevOtp) => {
      const nextOtp = [...prevOtp];
      for (let i = 0; i < text.length && index + i < props.length; i++) {
        nextOtp[index + i] = text[i];
      }

      return nextOtp;
    });

    if (nextFocus < lastIndex) {
      inputRefs.current[nextFocus + 1]?.focus();
    } else {
      inputRefs.current[lastIndex]?.focus();
    }
  };

  const handleChange = (e, index) => {
    const rawValue = e.target.value || "";
    // Ignore non-digit keyboard input while preserving native copy/paste/cut behavior.
    if (rawValue && !/\d/.test(rawValue)) {
      return;
    }
    updateFromText(index, rawValue);
  };

  const handleBeforeInput = (e) => {
    const inputType = e.nativeEvent?.inputType || "";
    if (!inputType.startsWith("insert")) {
      return;
    }

    // Let onPaste handle paste events to avoid duplicate updates
    // in browsers that fire both paste and beforeinput.
    if (inputType === "insertFromPaste") {
      return;
    }

    const insertedText = e.nativeEvent?.dataTransfer?.getData?.("text") || e.nativeEvent?.data || "";
    const normalizedInsertedText = insertedText.replace(/\D+/g, "");
    // Only intercept multi-character inserts (paste/autofill). Let single-char typing flow naturally.
    if (normalizedInsertedText.length <= 1) {
      return;
    }

    const selectedIndex = inputRefs.current.indexOf(e.target);
    if (selectedIndex < 0) {
      return;
    }

    e.preventDefault();
    updateFromText(selectedIndex, normalizedInsertedText);
  };
  const handleFocus = (e) => {
    e.target.select();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    let text = e.clipboardData.getData("text");
    // Remove all non-digit characters
    text = text.replaceAll(/\D+/g, "")
    // No digits, do nothing
    if (!text) {
      return;
    }
    const selectedIndex = inputRefs.current.indexOf(e.target);
    updateFromText(selectedIndex >= 0 ? selectedIndex : 0, text);
  };

  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    if (typeof onChangeRef.current === "function") {
      onChangeRef.current(otp.join(""));
    }
  }, [otp]);

  return (
    <div className="container">
      <form id="otp-form" className="flex gap-2">
        {otp.map((digit, index) => (
          <input
            id={"code&#8204;Input" + index}// The &#8204; is to confuse auto-complete
            key={index}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={digit}
            autoFocus={index === 0}
            onChange={(e) => handleChange(e, index)}
            onBeforeInput={handleBeforeInput}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onPaste={handlePaste}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            ref={(element) => (inputRefs.current[index] = element)}
            className="shadow-xs flex w-[38px] items-center justify-center rounded-lg border border-stroke bg-transparent p-2 text-center text-2xl font-medium text-gray-5 outline-none sm:text-4xl border-gray-700 dark:bg-white/5"
          />
        ))}
      </form>
    </div>
  );
}

OtpCodeEntry.propTypes = {
  onChange: PropTypes.func.isRequired,
  length: PropTypes.number.isRequired
}